import { UsersOutputProps } from 'types/UserTypes'
import { getDistance } from 'geolib'

/**
 * Deduplicates an array of users by userId, giving preference to users with location information.
 * If multiple users have the same userId, the one with location information is kept.
 * If both have location information, the first occurrence is kept.
 *
 * @param users Array of users to deduplicate
 * @returns Deduplicated array of users
 */
const dedupeUsersWithLocationPreference = (users: UsersOutputProps[]): UsersOutputProps[] => {
  const userMap = new Map<string, UsersOutputProps>()

  users.forEach((user) => {
    const existingUser = userMap.get(user.userId)

    // If user doesn't exist in map, add them
    if (!existingUser) {
      userMap.set(user.userId, user)
      return
    }

    // If existing user doesn't have location but new one does, replace
    const existingHasLocation =
      existingUser.userCommunityStudentPickupLocation || existingUser.userCommunityStudentDropoffLocation
    const newHasLocation = user.userCommunityStudentPickupLocation || user.userCommunityStudentDropoffLocation

    if (!existingHasLocation && newHasLocation) {
      userMap.set(user.userId, user)
    }
  })

  return Array.from(userMap.values())
}

/**
 * Sorts users by their distance from a reference point
 * Users without location information will be placed at the end
 */
const sortUsersByDistance = (
  users: UsersOutputProps[],
  referencePoint?: { lat: number; lng: number },
): (UsersOutputProps & { _distance: number })[] => {
  return [...users]
    .map((user) => {
      let distance = Infinity

      if (referencePoint) {
        if (user.userCommunityStudentDropoffLocation) {
          distance = getDistance(referencePoint, user.userCommunityStudentDropoffLocation)
        } else if (user.userCommunityStudentPickupLocation) {
          distance = getDistance(referencePoint, user.userCommunityStudentPickupLocation)
        }
      }

      return { ...user, _distance: distance }
    })
    .sort((a, b) => {
      // First sort by distance
      if (a._distance !== b._distance) {
        return a._distance - b._distance
      }
      // If distances are equal, sort alphabetically by userName
      return (a.userName || '').localeCompare(b.userName || '')
    })
}

/**
 * Filters users by community, deduplicates them, and sorts by distance from a reference user
 * @param allUsers - Array of all users to process
 * @param referenceUserId - ID of the user to calculate distance from (optional)
 * @param communityId - ID of the community to filter users by (optional)
 * @returns Processed and sorted array of users with distance information
 */
export const getCommunityUsersSortedByDistance = (
  allUsers: UsersOutputProps[],
  referenceUserId?: string,
  communityId?: string,
): (UsersOutputProps & { _distance: number })[] => {
  // 1. Filter users by community
  const communityUsers = communityId ? allUsers.filter((v) => v.userCommunityId === communityId) : allUsers

  // 2. Dedupe users
  const uniqueUsers = dedupeUsersWithLocationPreference(communityUsers)

  // 3. Get reference user's location for distance calculation if referenceUserId is provided
  let referencePoint: { lat: number; lng: number } | undefined
  if (referenceUserId) {
    const referenceUser = uniqueUsers.find((v) => v.userId === referenceUserId)
    referencePoint =
      referenceUser?.userCommunityStudentDropoffLocation || referenceUser?.userCommunityStudentPickupLocation
  }

  // 4. Sort users by distance followed by alphabetically by userName
  const sortedUsers = sortUsersByDistance(uniqueUsers, referencePoint)

  // 5. Filter out the reference user if referenceUserId is provided
  return referenceUserId ? sortedUsers.filter((v) => v.userId !== referenceUserId) : sortedUsers
}
